import { useTranslation } from "react-i18next";
import { Minus, Package, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Product, ProductQuantities } from "@/lib/types";

/** Most of one product a booking can take (the server allows 99). */
const MAX_PER_PRODUCT = 99;

/**
 * Products drawer inside the booking page. There is no shop: the customer
 * picks products here, they are paid with the wash and the crew brings
 * them. The picks live in the booking's own state (quantities), so the
 * date, slot, services and address being filled in stay as they are.
 */
export function BookingProductsSheet({
  open,
  onOpenChange,
  products,
  isLoading,
  quantities,
  onChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: Product[];
  isLoading: boolean;
  quantities: ProductQuantities;
  onChange: (productId: string, quantity: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  // Sold-out products go last so the top of the list can always be added.
  const ordered = [...products].sort((a, b) => Number(b.inStock) - Number(a.inStock));
  const count = products.reduce((sum, p) => sum + (quantities[p.id] || 0), 0);
  const total = products.reduce((sum, p) => sum + p.price * (quantities[p.id] || 0), 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        dir={isRtl ? "rtl" : "ltr"}
        className="max-h-[88dvh] flex flex-col gap-0 rounded-t-3xl p-0 sm:max-w-2xl sm:mx-auto"
        data-testid="booking-products-sheet"
      >
        {/* pr-12: the close button sits top-right in both directions. */}
        <SheetHeader className="shrink-0 pl-5 pr-12 pt-5 pb-3 text-start">
          <SheetTitle>{t("booking.products.sheetTitle")}</SheetTitle>
          <SheetDescription>{t("booking.products.sheetSubtitle")}</SheetDescription>
        </SheetHeader>

        <ul className="flex-1 min-h-0 overflow-y-auto px-5 pb-4 space-y-2">
          {isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="h-20 rounded-2xl bg-muted animate-pulse" />
            ))}
          {!isLoading && products.length === 0 && (
            <li className="py-10 text-center text-sm text-muted-foreground">
              {t("booking.products.empty")}
            </li>
          )}
          {ordered.map((product) => (
            <ProductRow
              key={product.id}
              product={product}
              isRtl={isRtl}
              quantity={quantities[product.id] || 0}
              onChange={(quantity) => onChange(product.id, quantity)}
            />
          ))}
        </ul>

        <div className="shrink-0 border-t border-border px-5 py-4">
          <Button
            type="button"
            className="w-full rounded-full"
            onClick={() => onOpenChange(false)}
            data-testid="booking-products-done"
          >
            {count > 0
              ? t("booking.products.doneWithTotal", {
                  count,
                  total: `${total.toFixed(3)} ${t("services.currency")}`,
                })
              : t("booking.products.done")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ProductRow({
  product,
  isRtl,
  quantity,
  onChange,
}: {
  product: Product;
  isRtl: boolean;
  quantity: number;
  onChange: (quantity: number) => void;
}) {
  const { t } = useTranslation();
  const name = isRtl ? product.nameAr : product.nameEn;
  const description = isRtl ? product.descriptionAr : product.descriptionEn;
  const max = Math.min(product.stockQuantity, MAX_PER_PRODUCT);
  const lowStock = product.inStock && product.stockQuantity <= 5;
  const details = [product.brand, product.size].filter(Boolean).join(" · ");

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-2xl border p-2.5 transition-colors",
        quantity > 0 ? "border-primary/60 bg-primary/5" : "border-border bg-card",
        !product.inStock && "opacity-60",
      )}
      data-testid={`booking-product-${product.id}`}
    >
      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-muted">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Package className="h-6 w-6 text-muted-foreground/40" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{name}</p>
        {(details || description) && (
          // bdi: sizes like "40×60 cm" keep their order inside Arabic text.
          <p className="line-clamp-1 text-xs text-muted-foreground">
            <bdi>{details || description}</bdi>
          </p>
        )}
        <p className="mt-0.5 text-sm">
          <span className="font-bold text-primary">{product.price.toFixed(3)}</span>{" "}
          <span className="text-[10px] text-muted-foreground">{t("services.currency")}</span>
          {!product.inStock && (
            <span className="ms-2 text-xs text-destructive">{t("booking.products.outOfStock")}</span>
          )}
          {lowStock && (
            <span className="ms-2 text-xs text-muted-foreground">
              {t("booking.products.lowStock", { count: product.stockQuantity })}
            </span>
          )}
        </p>
      </div>

      {product.inStock &&
        (quantity === 0 ? (
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-9 w-9 flex-shrink-0 rounded-full border-primary/40 text-primary"
            aria-label={t("booking.products.addTitle")}
            onClick={() => onChange(1)}
            data-testid={`booking-product-add-${product.id}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        ) : (
          <span className="inline-flex flex-shrink-0 items-center rounded-full bg-primary text-primary-foreground">
            <button
              type="button"
              aria-label="-"
              className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-primary-foreground/15"
              onClick={() => onChange(quantity - 1)}
              data-testid={`booking-product-minus-${product.id}`}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-5 text-center text-xs font-bold tabular-nums">{quantity}</span>
            <button
              type="button"
              aria-label="+"
              className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-primary-foreground/15 disabled:opacity-40"
              disabled={quantity >= max}
              onClick={() => onChange(quantity + 1)}
              data-testid={`booking-product-plus-${product.id}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
    </li>
  );
}
